<ng-container *transloco="let t">
  <div class="mx-auto my-4 flex w-fit max-w-full flex-col gap-y-7">
    @if (advancedSettings().strategyLabel) {
      <app-setting-row>
        <ng-container label>
          {{ t("advanced-settings-modal.strategy_label") }}
        </ng-container>
        <div class="flex min-w-0 items-center gap-2">
          <button
            iButton
            variant="secondary"
            class="min-w-0"
            [disabled]="isReadOnly()"
            (click)="openStrategyLabelModal()"
          >
            <IIcon name="edit" />
            <span class="truncate">{{ strategyLabelButtonLabel() }}</span>
          </button>
          @if (strategy().strategyLabel) {
            <button
              iButton
              variant="destructive"
              size="icon-sm"
              [disabled]="isReadOnly()"
              (click)="updateStrategyLabel('')"
            >
              <IIcon name="trash" />
            </button>
          }
        </div>
      </app-setting-row>
    }
    @if (advancedSettings().targetMinDailySpend) {
      <app-setting-row>
        <ng-container label>
          {{ t("strategy-page.min_daily_spend") }}
        </ng-container>

        <div class="flex min-w-0">
          <button
            iButton
            variant="secondary"
            class="min-w-0"
            testId="min-spend-val"
            (click)="openMinDailySpendModal()"
            [disabled]="isReadOnly()"
          >
            <IIcon name="edit" />
            <span class="truncate">{{
              strategy().minDailySpend ? strategy().minDailySpend + " " + currencyCode() : t("common.add")
            }}</span>
          </button>
        </div>
      </app-setting-row>
    }

    @if (advancedSettings().autoCampaign) {
      <app-toggle-auto-segment
        [isReadOnly]="isReadOnly()"
        [organizationId]="organizationId()"
        [strategy]="strategy()"
      />
    }

    @if (advancedSettings().productTargeting && strategy().campaignType !== CampaignType.SB) {
      <app-toggle-product-targeting
        [strategy]="strategy()"
        [organizationId]="organizationId()"
        [isReadOnly]="isReadOnly()"
        [segmentIndex]="segmentIndex()"
      />
    }

    @if (advancedSettings().aiPoweredTargeting && strategy()) {
      <app-strategy-page-toggle-ai-powered-targeting
        [strategy]="strategy()"
        [organizationId]="organizationId()"
        [isReadOnly]="isReadOnly()"
        [segmentIndex]="segmentIndex()"
      />
    }

    @if (advancedSettings().dayparting && strategy()) {
      <app-day-parting-inputs
        [disabled]="isReadOnly()"
        [strategy]="strategy()!"
        [locale]="locale()"
        textSize="text-sm"
      />
    }

    @if (advancedSettings().tosro && strategy()) {
      <app-toggle-tosro [strategy]="strategy()" [organizationId]="organizationId()" [isReadOnly]="isReadOnly()" />
    }

    @if (advancedSettings().boostAudience && strategy()) {
      <app-setting-row>
        <ng-container label>
          {{ t("advanced-settings-modal.boost_audience") }}
        </ng-container>
        <div class="flex min-w-0 items-center gap-2">
          @if (selectedBoostAudience()) {
            <button
              iButton
              variant="secondary"
              class="min-w-0"
              (click)="openBoostAudienceModal(true)"
              [disabled]="isReadOnly()"
            >
              @if (!isReadOnly()) {
                <IIcon name="edit" />
              }
              <span class="truncate">{{ selectedBoostAudience()?.audienceName }}</span>
            </button>
          }

          <IToggle
            [loading]="disableBoostAudienceLoading()"
            [disabled]="isReadOnly()"
            [checked]="!!strategy().boostAudienceId"
            (checkedChange)="openBoostAudienceModal(!!$event)"
          />
        </div>
      </app-setting-row>
    }

    @if (advancedSettings().boostAmazonBusiness && strategy()) {
      <app-toggle-business-boost
        [strategy]="strategy()"
        [organizationId]="organizationId()"
        [isReadOnly]="isReadOnly()"
      />
    }

    @if (advancedSettings().onlyB2B) {
      <app-setting-row>
        <ng-container label>
          {{ t("advanced-settings-modal.amazon_business_only") }}
          <IIcon
            name="infoCircle"
            class="text-muted-foreground size-4"
            [iTooltip]="t('advanced-settings-modal.amazon_business_only_description')"
            iTooltipPlacement="right-center"
          />
        </ng-container>
        <IToggle [checked]="true" [disabled]="true" testid="only-b2b" />
      </app-setting-row>
    }

    @if (advancedSettings().frequencyCap && frequencyCapControl()) {
      <app-setting-row>
        <ng-container label>
          {{ t("advanced-settings-modal.frequency_cap") }}
        </ng-container>
        <IFormField class="w-42">
          <div iInputGroup>
            <input
              iInputGroupInput
              type="number"
              size="sm"
              [formControl]="frequencyCapControl()!"
              [max]="MAX_FREQUENCY_CAP"
            />
            <button
              iButton
              iInputGroupSuffix
              size="xs"
              [disabled]="frequencyCapControl()!.pristine || frequencyCapControl()!.invalid || isReadOnly()"
              [loading]="isUpdatingFrequencyCap()"
              (click)="updateFrequencyCap()"
            >
              {{ t("common.save") }}
            </button>
          </div>
          <IError>{{ t("advanced-settings-modal.frequency_cap_max_error", [[MAX_FREQUENCY_CAP]]) }}</IError>
        </IFormField>
      </app-setting-row>
    }

    @if (advancedSettings().agencyFee && agencyFee() !== undefined) {
      <app-setting-row>
        <ng-container label>
          {{ t("dsp.agency_fee") }}
        </ng-container>
        <div class="type-body text-foreground">{{ agencyFee() }} %</div>
      </app-setting-row>
    }

    @if (advancedSettings().boostPlacementTop && strategy()) {
      <app-toggle-boost-placement-top
        [strategy]="strategy()"
        [organizationId]="organizationId()"
        [isReadOnly]="isReadOnly()"
      />
    }
  </div>

  @if (advancedSettings().strategyGroup && strategyGroup()) {
    <div class="my-8 flex flex-wrap items-center justify-center gap-4">
      @if (!hasBrandStrategies()) {
        <button
          iButton
          variant="secondary"
          size="md"
          (click)="openSubStrategyCreationPage(StrategyType.BRAND)"
          [disabled]="isReadOnly()"
        >
          {{ t("sp-strategy-group-page.create_brand_defense_strategy") }}
        </button>
      }
      @if (!hasKeywordStrategies()) {
        <button
          iButton
          variant="secondary"
          size="md"
          (click)="openSubStrategyCreationPage(StrategyType.KEYWORD)"
          [disabled]="isReadOnly()"
        >
          {{ t("sp-strategy-group-page.create_focus_strategy") }}
        </button>
      }
    </div>
  }

  <ng-template #updateMinDailySpendModal>
    <div>
      <IFormField>
        <div iInputGroup>
          <input
            iInputGroupInput
            class="w-full"
            data-testid="min-spend-input"
            type="number"
            [formControl]="minDailySpend()"
            name="minDailySpend"
          />
          <IBadge iInputGroupSuffix [label]="currencyCode()" color="gray" />
        </div>
        <IError>{{ t("advanced-settings-modal.min_daily_spend_error", [maxMinDailySpend()]) }}</IError>
      </IFormField>
      <div class="mt-4 flex w-full justify-between">
        <button
          iButton
          variant="secondary"
          size="md"
          (click)="minDailySpend().setValue(null); dailySpendModalRef.close()"
        >
          {{ t("common.cancel") }}
        </button>
        <div class="flex flex-1 justify-end">
          @if (strategy().minDailySpend) {
            <button
              iButton
              variant="destructive"
              [disabled]="minDailySpend().value === 0"
              class="mr-2"
              [loading]="updateMinDailySpendLoading()"
              (click)="updateMinDailySpend(0)"
            >
              <IIcon name="trash" />
              {{ t("common.remove") }}
            </button>
          }
          <button
            iButton
            testId="btn-spend-confirm"
            class="w-2/4"
            [disabled]="minDailySpend().invalid"
            [loading]="updateMinDailySpendLoading()"
            (click)="updateMinDailySpend(minDailySpend().value ?? 0)"
          >
            {{ t("common.update") }}
          </button>
        </div>
      </div>
    </div>
  </ng-template>
</ng-container>
